import { useEffect, useState } from 'react';

import { getGames, getLeagues, getStandings } from '../api';
import LeagueDisplay from '../components/LeagueDisplay';
import ResultsTable from '../components/ResultsTable';

function LiveScreen({ isPaused, manualNav, setManualNav }) {
  const PROGRESS_BAR_UPDATE_INTERVAL = 100;
  const storedInterval = localStorage.getItem('leagueSwitchInterval');
  const LEAGUE_SWITCH_INTERVAL = storedInterval ? parseInt(storedInterval, 10) * 1000 : 10000;
  const storedNumLeagues = localStorage.getItem('numLeaguesToShow');
  const numLeaguesToShow = storedNumLeagues ? parseInt(storedNumLeagues, 10) : 2;

  const [leagues, setLeagues] = useState([]);
  const [leagueIndices, setLeagueIndices] = useState([]);
  const [nextUpdateIndex, setNextUpdateIndex] = useState(0);
  const [standings, setStandings] = useState({});
  const [games, setGames] = useState({});
  const [progress, setProgress] = useState(0);
  const [countdown, setCountdown] = useState(LEAGUE_SWITCH_INTERVAL / 1000);

  useEffect(() => {
    const fetchLeaguesData = async () => {
      const response = await getLeagues();
      setLeagues(response.data);
      if (response.data.length > 0) {
        const initialIndices = Array.from({ length: Math.min(numLeaguesToShow, response.data.length) }, (_, i) => i);
        setLeagueIndices(initialIndices);
      }
    };
    fetchLeaguesData();
  }, [numLeaguesToShow]);

  useEffect(() => {
    const fetchLeagueData = async (leagueId) => {
      const standingsResponse = await getStandings(leagueId);
      setStandings(prev => ({ ...prev, [leagueId]: standingsResponse.data }));
      if (numLeaguesToShow !== 4) {
        const gamesResponse = await getGames(leagueId);
        setGames(prev => ({ ...prev, [leagueId]: gamesResponse.data.filter(g => g.home_score !== null) }));
      }
    };

    if (leagues.length > 0) {
      leagueIndices.forEach(index => fetchLeagueData(leagues[index].id));
    }
  }, [leagues, leagueIndices, numLeaguesToShow]);

  useEffect(() => {
    if (leagues.length > 0 && !isPaused) {
      const interval = setInterval(() => {
        if (leagues.length > numLeaguesToShow) {
          setLeagueIndices(prevIndices => {
            const newIndices = [...prevIndices];
            let nextLeague = (Math.max(...newIndices) + 1) % leagues.length;
            while (newIndices.includes(nextLeague)) {
              nextLeague = (nextLeague + 1) % leagues.length;
            }
            newIndices[nextUpdateIndex] = nextLeague;
            return newIndices;
          });
          setNextUpdateIndex(prev => (prev + 1) % numLeaguesToShow);
        }
      }, LEAGUE_SWITCH_INTERVAL);

      return () => clearInterval(interval);
    }
  }, [leagues, leagueIndices, LEAGUE_SWITCH_INTERVAL, nextUpdateIndex, numLeaguesToShow, isPaused]);

  useEffect(() => {
    if (isPaused) {
      return;
    }
    setProgress(0);
    setCountdown(LEAGUE_SWITCH_INTERVAL / 1000);

    const progressInterval = setInterval(() => {
      setProgress((prevProgress) => {
        if (prevProgress >= 100) {
          return 0;
        }
        return prevProgress + (PROGRESS_BAR_UPDATE_INTERVAL / LEAGUE_SWITCH_INTERVAL) * 100;
      });
    }, PROGRESS_BAR_UPDATE_INTERVAL);

    const countdownInterval = setInterval(() => {
      setCountdown((prevCountdown) => (prevCountdown > 0 ? prevCountdown - 1 : 0));
    }, 1000);

    return () => {
      clearInterval(progressInterval);
      clearInterval(countdownInterval);
    };
  }, [leagueIndices, LEAGUE_SWITCH_INTERVAL, isPaused]);

  useEffect(() => {
    if (!manualNav) {
      return;
    }

    if (manualNav === 'next') {
      if (leagues.length > numLeaguesToShow) {
        setLeagueIndices(prevIndices => {
          const newIndices = [...prevIndices];
          let nextLeague = (Math.max(...newIndices) + 1) % leagues.length;
          while (newIndices.includes(nextLeague)) {
            nextLeague = (nextLeague + 1) % leagues.length;
          }
          newIndices[nextUpdateIndex] = nextLeague;
          return newIndices;
        });
        setNextUpdateIndex(prev => (prev + 1) % numLeaguesToShow);
      }
    } else if (manualNav === 'prev') {
      if (leagues.length > numLeaguesToShow) {
        setLeagueIndices(prevIndices => {
          const newIndices = [...prevIndices];
          const slotToReplace = (nextUpdateIndex - 1 + numLeaguesToShow) % numLeaguesToShow;

          let prevLeagueIndex = (Math.min(...prevIndices) - 1 + leagues.length) % leagues.length;
          while (newIndices.includes(prevLeagueIndex)) {
            prevLeagueIndex = (prevLeagueIndex - 1 + leagues.length) % leagues.length;
          }

          newIndices[slotToReplace] = prevLeagueIndex;
          return newIndices;
        });
        setNextUpdateIndex(prev => (prev - 1 + numLeaguesToShow) % numLeaguesToShow);
      }
    }

    setManualNav(null);
    setProgress(0);
    setCountdown(LEAGUE_SWITCH_INTERVAL / 1000);
  }, [manualNav, leagues, numLeaguesToShow, nextUpdateIndex, setManualNav, LEAGUE_SWITCH_INTERVAL]);

  const displayedLeagues = leagueIndices.map(index => leagues[index]);

  const findNextLeagueIndex = () => {
    if (leagues.length <= numLeaguesToShow) {
      return null;
    }
    let nextIndex = (Math.max(...leagueIndices) + 1) % leagues.length;
    while (leagueIndices.includes(nextIndex)) {
      nextIndex = (nextIndex + 1) % leagues.length;
    }
    return nextIndex;
  };

  const nextLeagueIndex = findNextLeagueIndex();
  const nextLeague = nextLeagueIndex !== null ? leagues[nextLeagueIndex] : null;

  return (
    <div className={`live-container ${numLeaguesToShow === 4 ? 'live-grid' : ''}`}>
      {displayedLeagues.map((league, index) => (
        <div key={league.id} className="live-league">
          <LeagueDisplay
            league={league}
            standings={standings[league.id] || []}
            progress={leagues.length > numLeaguesToShow && index === nextUpdateIndex ? progress : undefined}
            nextLeague={nextLeague}
            countdown={countdown}
            isLivePage={true}
          />
          {numLeaguesToShow !== 4 && games[league.id] && games[league.id].length > 0 && <h3>Results</h3>}
          {numLeaguesToShow !== 4 && <ResultsTable games={games[league.id] || []} showButtons={false} />}
        </div>
      ))}
    </div>
  );
}

export default LiveScreen;
